import React, { useState, useEffect } from 'react';
import styles from './Home.module.css';
import { authService, User } from '../../services/auth.service';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ChevronDownIcon,
  NavHomeIcon,
  NavUsersIcon,
  NavPlayerIcon,
  NavAgentIcon,
  NavDistributorIcon,
  NavSubDistributorIcon,
  NavShopIcon,
  NavTransactionsIcon,
  NavOnlineTransactionsIcon,
  NavProviderBalanceIcon,
  NavWithdrawReviewIcon,
  NavGameHistoryIcon,
  NavGameManagementIcon,
  NavRaffleIcon,
  NavPromoIcon,
  NavAtmIcon,
  NavConfigurationsIcon,
  NavPermissionsIcon,
  NavNotificationsIcon,
  NavSupportIcon,
} from '../../assets/icons';
import { getCurrentUserBalance } from '../../services/getCurrentUserBalance.service';
import ChangePasswordModal from '../User/ChangePasswordModal';
import { ChangePasswordIcon } from '../../assets/icons';
import logoGif from '../../assets/images/logo.gif';
import successAnimation from '../../assets/images/flow888.json';

import {
  balanceSubscriptionService,
  startBalanceSubscription,
  stopBalanceSubscription,
  BalanceUpdatedData
} from '../../services/balanceSubscription.service';
import { getUserById, UserById } from '../../services/getUserById.service';
import Lottie from 'lottie-react';
import nova7Logo from '../../assets/images/nova7.png';
import gg888Logo from '../../assets/images/gg888.png';
import crackerLogo from '../../assets/images/cracker.png';
import { getDomainBrand } from '../../utils/getDomainBrand';
import bgFlow888 from '../../assets/images/background-2.webp';
import { Loading } from '../Loading';
import bgNova7 from '../../assets/images/novaBg.png';
import bgGG888 from '../../assets/images/ggbg.png';
import bgCracker from '../../assets/images/crackerbg.png';
import { EnvironmentProvider } from '../../contexts/EnvironmentContext';

const BRAND_BACKGROUNDS: Record<string, string> = {
  flow888: bgFlow888,
  nova7: bgNova7,
  gg888: bgGG888,
  crackerjackpot: bgCracker,
};

const BRAND_LOGOS: Record<string, string> = {
  nova7: nova7Logo,
  gg888: gg888Logo,
  crackerjackpot: crackerLogo,
};
interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState<User | UserById | null>(null);
  const brand = getDomainBrand();
  const [isUserManagementExpanded, setIsUserManagementExpanded] =
    useState(false);
  const [isMobileUserModalOpen, setIsMobileUserModalOpen] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

  useEffect(() => {
    // Get user data from auth service (refresh on mount)
    const currentUser = authService.getStoredUser();
    if (currentUser) {
      getUserById(currentUser.id).then((user) => {
        if (user) {
          setUser(user);
          authService.setCurrentUser(user);
        }
      });

      // Start balance subscription
      startBalanceSubscription({
        onUpdate: (data: BalanceUpdatedData) => {
          const storedUser = authService.getStoredUser();
          if (storedUser && data.Balance) {
            const updatedUser = { ...storedUser, Balance: data.Balance };
            authService.setCurrentUser(updatedUser);
            setUser(updatedUser);
          }
        },
        onError: (error) => {
          console.error('❌ Balance subscription error:', error);
          // Optionally implement fallback polling here
        },
        onConnect: () => {
          console.log('🔌 Balance subscription connected');
        },
        onDisconnect: () => {
          console.log('🔌 Balance subscription disconnected');
        }
      });
    } else {
      navigate('/login');
    }

    // Keep the existing balance update event listener for compatibility
    const handleBalanceUpdate = async (event: Event) => {
      const storedUser = authService.getStoredUser();
      if (storedUser) {
        try {
          const balance = await getCurrentUserBalance();

          const oldBalance = storedUser.Balance;

          const updatedUser = { ...storedUser, Balance: balance };
          authService.setCurrentUser(updatedUser);
          setUser(updatedUser);
        } catch (err) {
          console.error('❌ Failed to update balance from event:', err);
        }
      } else {
        console.log(
          '⚠️ No stored user found when handling balance update event',
        );
      }
    };

    window.addEventListener('balanceUpdated', handleBalanceUpdate);

    return () => {
      // Clean up subscription and event listener
      stopBalanceSubscription();
      window.removeEventListener('balanceUpdated', handleBalanceUpdate);
    };
  }, []);

  useEffect(() => {
    // Auto-expand User Management dropdown if on a user management route
    if (isUserManagementRoute(location.pathname)) {
      setIsUserManagementExpanded(true);
    }
  }, [location.pathname]);

  // Handle screen resize to close mobile sidebar when switching to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 820 && isMobileSidebarOpen) {
        setIsMobileSidebarOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMobileSidebarOpen]);

  const handleLogout = async () => {
    await stopBalanceSubscription(); // Stop subscription before logout
    await authService.logout();
    navigate('/login');
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    // Close mobile sidebar after navigation
    setIsMobileSidebarOpen(false);
  };

  const handleUserManagementToggle = () => {
    setIsUserManagementExpanded(!isUserManagementExpanded);
  };

  const handleMobileUserModalToggle = () => {
    setIsMobileUserModalOpen(!isMobileUserModalOpen);
  };

  const handleMobileSidebarToggle = () => {
    setIsMobileSidebarOpen(!isMobileSidebarOpen);
  };

  const handleMobileSidebarClose = () => {
    setIsMobileSidebarOpen(false);
  };

  const handlePasswordModalOpen = () => {
    setIsPasswordModalOpen(true);
    setIsMobileSidebarOpen(false);
  };

  const handlePasswordModalClose = () => {
    setIsPasswordModalOpen(false);
  };

  const isActiveRoute = (path: string) => {
    return location.pathname === path;
  };

  const isUserManagementRoute = (path: string) => {
    return [
      '/distributor',
      '/shop',
      '/player',
      '/sub-distributor',
      '/agent',
    ].includes(path);
  };

  // Role-based navigation visibility functions
  const shouldShowUserManagement = (roleName: string) => {
    return roleName !== 'USER';
  };

  const shouldShowDistributor = (roleName: string) => {
    return roleName === 'ADMIN';
  };

  const shouldShowSubDistributor = (roleName: string) => {
    return ['ADMIN', 'DISTRIBUTOR'].includes(roleName);
  };

  const shouldShowAgent = (roleName: string) => {
    return ['ADMIN', 'DISTRIBUTOR', 'SUBDISTRIBUTOR'].includes(roleName);
  };

  const shouldShowShop = (roleName: string) => {
    return ['ADMIN', 'DISTRIBUTOR', 'SUBDISTRIBUTOR', 'AGENT'].includes(
      roleName,
    );
  };

  const shouldShowPlayer = (roleName: string) => {
    return ['ADMIN', 'DISTRIBUTOR', 'SUBDISTRIBUTOR', 'AGENT', 'SHOP'].includes(
      roleName,
    );
  };

  const shouldShowPermissions = (roleName: string) => {
    return roleName === 'ADMIN';
  };

  // Show loading or redirect if no user data
  if (!user) {
    return (
      <Loading
        type="spinner"
        size="large"
        text="Loading..."
        fullScreen
      />
    );
  }

  // Calculate total balance from user's balance data
  const totalBalance = user.Balance
    ? user.Balance.amount + user.Balance.credit
    : user.credit || 0;

  const userRole = user.role.name;
  // ATM managers get the ATM Controller and nothing else.
  const isAtmManager = userRole === 'ATM_MANAGER';
  const canSeeAtmController = userRole === 'ADMIN' || isAtmManager;

  const formatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });

  return (
    <div className={styles['home-root']}>
      {/* Mobile Header */}
      <header className={styles['mobile-header']}>
        <div className={styles['mobile-header-content']}>
          <div
            className={styles['mobile-user-info']}
            onClick={handleMobileUserModalToggle}
          >
            <div className={styles['mobile-welcome']}>
              <div className={styles['mobile-welcome-text']}>Welcome Back,</div>
              <div className={styles['mobile-username']}>
                {user.name || user.username}
              </div>
            </div>
          </div>
          <button
            className={styles['mobile-hamburger']}
            onClick={handleMobileSidebarToggle}
          >
            <div className={styles['mobile-hamburger-icon']}>
              {isMobileSidebarOpen ? (
                // Close icon (X)
                <>
                  <div
                    className={`${styles['mobile-hamburger-line']} ${styles['close-line-1']}`}
                  ></div>
                  <div
                    className={`${styles['mobile-hamburger-line']} ${styles['close-line-2']}`}
                  ></div>
                  <div
                    className={`${styles['mobile-hamburger-line']} ${styles['close-line-3']}`}
                  ></div>
                </>
              ) : (
                // Hamburger icon (3 lines)
                <>
                  <div className={styles['mobile-hamburger-line']}></div>
                  <div className={styles['mobile-hamburger-line']}></div>
                  <div className={styles['mobile-hamburger-line']}></div>
                </>
              )}
            </div>
          </button>
        </div>
      </header>

      {/* Mobile User Modal Overlay */}
      <div
        className={`${styles['mobile-user-modal-overlay']} ${isMobileUserModalOpen ? styles.open : ''
          }`}
        onClick={handleMobileUserModalToggle}
      ></div>

      {/* Mobile User Profile Modal */}
      <div
        className={`${styles['mobile-user-modal']} ${isMobileUserModalOpen ? styles.open : ''
          }`}
      >
        <div className={styles['mobile-user-modal-header']}>
          <button
            className={styles['mobile-user-modal-close']}
            onClick={handleMobileUserModalToggle}
          >
            ×
          </button>
          {/* <img
            src={logoGif}
            alt="Flow 888"
            style={{ height: '100px', width: 'auto' }}
          /> */}
          {brand === 'flow888' ? (
            <Lottie
              style={{ height: '100px', width: 'auto' }}
              animationData={successAnimation}
              loop
              autoplay
            />
          ) : (
            <img
              src={BRAND_LOGOS[brand]}
              alt={brand}
              style={{ height: brand === 'nova7' ? '50px' : '100px', width: 'auto' }}
            />
          )}
          <div className={styles['mobile-user-modal-welcome']}>
            Welcome Back,
            <br />
            <span style={{ fontWeight: 600, fontSize: '16px' }}>
              {user.name || user.username}
            </span>
          </div>
          <div className={styles['mobile-user-modal-balance']}>
            ${totalBalance.toFixed(2)}
          </div>
          <div className={styles['mobile-user-modal-balance-label']}>
            Total Balance
          </div>
        </div>
        <div className={styles['mobile-user-modal-content']}>
          <div className={styles['mobile-user-modal-actions']}>
            <button
              className={styles['mobile-user-modal-action']}
              onClick={handlePasswordModalOpen}
            >
              🔒 Change Password
            </button>
            <button
              className={styles['mobile-user-modal-action']}
              onClick={handleLogout}
            >
              🚪 Log Out
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      <div
        className={`${styles['mobile-sidebar-overlay']} ${isMobileSidebarOpen ? styles.open : ''
          }`}
        onClick={handleMobileSidebarClose}
      ></div>

      {/* Mobile Sidebar */}
      <div
        className={`${styles['mobile-sidebar']} ${isMobileSidebarOpen ? styles.open : ''
          }`}
      >
        <div className={styles['mobile-sidebar-header']}>
          <button
            className={[styles['mobile-sidebar-close'], !isMobileSidebarOpen ? styles.hide : ''].join(' ')}
            onClick={handleMobileSidebarClose}
          >
            ×
          </button>
          {brand === 'flow888' ? (
            <Lottie
              style={{ height: '100px', width: 'auto' }}
              animationData={successAnimation}
              loop
              autoplay
            />
          ) : (
            <img
              src={BRAND_LOGOS[brand]}
              alt={brand}
              style={{ height: brand === 'nova7' ? '50px' : '100px', width: 'auto' }}
            />
          )}
          <div className={styles['mobile-sidebar-welcome']}>
            Welcome Back,
            <br />
            <span style={{ fontWeight: 600, fontSize: '16px' }}>
              {user.name || user.username}
            </span>
          </div>
          <div className={styles['mobile-sidebar-balance']}>
            ${totalBalance.toFixed(2)}
          </div>
          <div className={styles['mobile-sidebar-balance-label']}>
            Total Balance
          </div>
        </div>
        <nav className={styles['mobile-sidebar-nav']}>
          <ul>
            {!isAtmManager && (<>
            <li
              className={isActiveRoute('/home') ? styles.active : ''}
              onClick={() => handleNavigation('/home')}
            >
              <NavHomeIcon
                width={20}
                height={20}
                color={isActiveRoute('/home') ? '#313485' : '#fff'}
              />
              Home
            </li>
            {shouldShowUserManagement(userRole) && (
              <li
                className={`${styles['user-management-item']} ${isUserManagementRoute(location.pathname)
                  ? styles['dropdown-open']
                  : ''
                  }`}
                onClick={handleUserManagementToggle}
              >
                <NavUsersIcon
                  width={20}
                  height={20}
                  color={
                    isUserManagementRoute(location.pathname) ? '#fff' : '#fff'
                  }
                />
                User Management
                <ChevronDownIcon
                  width={20}
                  height={12}
                  color={
                    isUserManagementRoute(location.pathname) ? '#fff' : '#fff'
                  }
                />
              </li>
            )}
            {isUserManagementExpanded && shouldShowUserManagement(userRole) && (
              <>
                {shouldShowDistributor(userRole) && (
                  <li
                    className={`${styles['dropdown-item']} ${isActiveRoute('/distributor') ? styles.active : ''
                      }`}
                    onClick={() => handleNavigation('/distributor')}
                  >
                    <NavDistributorIcon
                      width={20}
                      height={20}
                      color={isActiveRoute('/distributor') ? '#313485' : '#fff'}
                    />
                    Distributor
                  </li>
                )}
                {shouldShowSubDistributor(userRole) && (
                  <li
                    className={`${styles['dropdown-item']} ${isActiveRoute('/sub-distributor') ? styles.active : ''
                      }`}
                    onClick={() => handleNavigation('/sub-distributor')}
                  >
                    <NavSubDistributorIcon
                      width={20}
                      height={20}
                      color={
                        isActiveRoute('/sub-distributor') ? '#313485' : '#fff'
                      }
                    />
                    Sub Distributor
                  </li>
                )}
                {shouldShowAgent(userRole) && (
                  <li
                    className={`${styles['dropdown-item']} ${isActiveRoute('/agent') ? styles.active : ''
                      }`}
                    onClick={() => handleNavigation('/agent')}
                  >
                    <NavAgentIcon
                      width={20}
                      height={20}
                      color={isActiveRoute('/agent') ? '#313485' : '#fff'}
                    />
                    Agent
                  </li>
                )}
                {shouldShowShop(userRole) && (
                  <li
                    className={`${styles['dropdown-item']} ${isActiveRoute('/shop') ? styles.active : ''
                      }`}
                    onClick={() => handleNavigation('/shop')}
                  >
                    <NavShopIcon
                      width={20}
                      height={20}
                      color={isActiveRoute('/shop') ? '#313485' : '#fff'}
                    />
                    Shop
                  </li>
                )}
                {shouldShowPlayer(userRole) && (
                  <li
                    className={`${styles['dropdown-item']} ${isActiveRoute('/player') ? styles.active : ''
                      }`}
                    onClick={() => handleNavigation('/player')}
                  >
                    <NavPlayerIcon
                      width={20}
                      height={20}
                      color={isActiveRoute('/player') ? '#313485' : '#fff'}
                    />
                    Player
                  </li>
                )}
              </>
            )}
            <li
              className={isActiveRoute('/transactions') ? styles.active : ''}
              onClick={() => handleNavigation('/transactions')}
            >
              <NavTransactionsIcon
                width={20}
                height={20}
                color={isActiveRoute('/transactions') ? '#313485' : '#fff'}
              />
              Transactions
            </li>
            {(brand !== 'flow888' || userRole === 'ADMIN') && (
              <li
                className={isActiveRoute('/online-transactions') ? styles.active : ''}
                onClick={() => handleNavigation('/online-transactions')}
              >
                <NavOnlineTransactionsIcon
                  width={20}
                  height={20}
                  color={isActiveRoute('/online-transactions') ? '#313485' : '#fff'}
                />
                Online Transactions
              </li>
            )}
            {userRole !== 'SHOP' && (

              <li
                className={isActiveRoute('/game-history') ? styles.active : ''}
                onClick={() => handleNavigation('/game-history')}
              >
                <NavGameHistoryIcon
                  width={20}
                  height={20}
                  color={isActiveRoute('/game-history') ? '#313485' : '#fff'}
                />
                Game History
              </li>
            )}
            {(userRole === 'ADMIN' ||
              authService.hasPermission('game.update')) && (
                <li
                  className={
                    isActiveRoute('/game-management') ? styles.active : ''
                  }
                  onClick={() => handleNavigation('/game-management')}
                >
                  <NavGameManagementIcon
                    width={20}
                    height={20}
                    color={isActiveRoute('/game-management') ? '#313485' : '#fff'}
                  />
                  Game Management
                </li>
              )}
            </>)}
            {canSeeAtmController && (
                <li
                  className={
                    isActiveRoute('/atm-controller') ? styles.active : ''
                  }
                  onClick={() => handleNavigation('/atm-controller')}
                >
                  <NavAtmIcon
                    width={20}
                    height={20}
                    color={isActiveRoute('/atm-controller') ? '#313485' : '#fff'}
                  />
                  ATM Controller
                </li>
              )}
            {!isAtmManager && (<>
            {userRole === 'ADMIN' && (
                <li
                  className={
                    isActiveRoute('/atm-history') ? styles.active : ''
                  }
                  onClick={() => handleNavigation('/atm-history')}
                >
                  <NavAtmIcon
                    width={20}
                    height={20}
                    color={isActiveRoute('/atm-history') ? '#313485' : '#fff'}
                  />
                  ATM History
                </li>
              )}
            {userRole === 'ADMIN' && (
              <li
                className={
                  isActiveRoute('/provider-balance') ? styles.active : ''
                }
                onClick={() => handleNavigation('/provider-balance')}
              >
                <span className={styles['menu-icon']}>
                  <NavProviderBalanceIcon
                    width={20}
                    height={20}
                    color={
                      isActiveRoute('/provider-balance') ? '#313485' : '#fff'
                    }
                  />
                </span>
                Provider Balance
              </li>
            )}
            {userRole === 'ADMIN' && (
              <li
                className={
                  isActiveRoute('/configurations') ? styles.active : ''
                }
                onClick={() => handleNavigation('/configurations')}
              >
                <span className={styles['menu-icon']}>
                  <NavConfigurationsIcon
                    width={20}
                    height={20}
                    color={
                      isActiveRoute('/configurations') ? '#313485' : '#fff'
                    }
                  />
                </span>
                Configurations
              </li>
            )}
            {authService.hasPermission('bonus.read') && (
              <li
                className={
                  isActiveRoute('/promo') ? styles.active : ''
                }
                onClick={() => handleNavigation('/promo')}
              >
                <span className={styles['menu-icon']}>
                  <NavPromoIcon
                    width={20}
                    height={20}
                    color={
                      isActiveRoute('/promo') ? '#313485' : '#fff'
                    }
                  />
                </span>
                Promo
              </li>
            )}
            {authService.hasPermission('bonus.read') && (
              <li
                className={
                  isActiveRoute('/raffle') ? styles.active : ''
                }
                onClick={() => handleNavigation('/raffle')}
              >
                <span className={styles['menu-icon']}>
                  <NavRaffleIcon
                    width={20}
                    height={20}
                    color={
                      isActiveRoute('/raffle') ? '#313485' : '#fff'
                    }
                  />
                </span>
                Raffle
              </li>
            )}
            {userRole === 'ADMIN' && (
              <li
                className={
                  isActiveRoute('/notifications') ? styles.active : ''
                }
                onClick={() => handleNavigation('/notifications')}
              >
                <span className={styles['menu-icon']}>
                  <NavNotificationsIcon
                    width={20}
                    height={20}
                    color={
                      isActiveRoute('/notifications') ? '#313485' : '#fff'
                    }
                  />
                </span>
                Notifications
              </li>
            )}
            {shouldShowPermissions(userRole) && (
              <li
                className={isActiveRoute('/permissions') ? styles.active : ''}
                onClick={() => handleNavigation('/permissions')}
              >
                <NavPermissionsIcon
                  width={20}
                  height={20}
                  color={isActiveRoute('/permissions') ? '#313485' : '#fff'}
                />
                Permissions
              </li>
            )}
            <li
              className={isActiveRoute('/support') ? styles.active : ''}
              onClick={() => handleNavigation('/support')}
            >
              <NavSupportIcon
                width={20}
                height={20}
                color={isActiveRoute('/support') ? '#313485' : '#fff'}
              />
              Support
            </li>
            {shouldShowPermissions(userRole) && (
              <li
                className={isActiveRoute('/withdraw-review') ? styles.active : ''}
                onClick={() => handleNavigation('/withdraw-review')}
              >
                <NavWithdrawReviewIcon
                  width={20}
                  height={20}
                  color={isActiveRoute('/withdraw-review') ? '#313485' : '#fff'}
                />
                Withdraw Review
              </li>
            )}
            </>)}
          </ul>
        </nav>
        <div className={styles['mobile-sidebar-footer']}>
          <button onClick={handleLogout}>Log Out</button>
          <button onClick={handlePasswordModalOpen}>Change Password</button>
        </div>
      </div>

      {/* Desktop Header */}
      <header className={styles['main-header']} style={{ backgroundImage: `url(${BRAND_BACKGROUNDS[brand]})` }}></header>

      {/* Content Wrapper */}
      <div className={styles['content-wrapper']}>
        {/* Desktop Sidebar */}
        <aside className={styles.sidebar}>
          <div className={styles['sidebar-header']}>
            {/* <img
              src={logoGif}
              alt="Flow 888"
              style={{ height: '100px', width: 'auto' }}
            /> */}
            {brand === 'flow888' ? (
              <Lottie
                style={{ height: '100px', width: 'auto' }}
                animationData={successAnimation}
                loop
                autoplay
              />
            ) : (
              <img
                src={BRAND_LOGOS[brand]}
                alt={brand}
                style={{ height:brand === 'nova7' ? '50px' : '100px', width: 'auto' }}
              />
            )}
            <div className={styles.welcome}>
              Welcome Back,
              <br />
              <span style={{ fontWeight: 500, fontSize: '18px' }}>
                {user.name || user.username}
              </span>
            </div>
            <div className={styles.balance}>{formatter.format(totalBalance)}</div>
            <div className={styles['balance-label']}>Total Balance</div>
          </div>
          <nav className={styles['sidebar-nav']}>
            <ul>
              {!isAtmManager && (<>
              <li
                className={isActiveRoute('/home') ? styles.active : ''}
                onClick={() => handleNavigation('/home')}
              >
                <span className={styles['menu-icon']}>
                  <NavHomeIcon
                    width={20}
                    height={20}
                    color={isActiveRoute('/home') ? '#313485' : '#fff'}
                  />
                </span>
                Home
              </li>
              {shouldShowUserManagement(userRole) && (
                <li
                  className={`${styles['user-management-item']} ${isUserManagementRoute(location.pathname)
                    ? styles['dropdown-open']
                    : ''
                    }`}
                  onClick={handleUserManagementToggle}
                >
                  <span className={styles['menu-icon']}>
                    <NavUsersIcon
                      width={20}
                      height={20}
                      color={
                        isUserManagementRoute(location.pathname)
                          ? '#fff'
                          : '#fff'
                      }
                    />
                  </span>
                  User Management
                  <span className={styles['chevron-icon']}>
                    <ChevronDownIcon
                      width={26}
                      height={15}
                      color={
                        isUserManagementRoute(location.pathname)
                          ? '#fff'
                          : '#fff'
                      }
                    />
                  </span>
                </li>
              )}
              {isUserManagementExpanded &&
                shouldShowUserManagement(userRole) && (
                  <>
                    {shouldShowDistributor(userRole) && (
                      <li
                        className={`${styles['dropdown-item']} ${isActiveRoute('/distributor') ? styles.active : ''
                          }`}
                        onClick={() => handleNavigation('/distributor')}
                      >
                        <span className={styles['menu-icon']}>
                          <NavDistributorIcon
                            width={20}
                            height={20}
                            color={
                              isActiveRoute('/distributor') ? '#313485' : '#fff'
                            }
                          />
                        </span>
                        Distributor
                      </li>
                    )}
                    {shouldShowSubDistributor(userRole) && (
                      <li
                        className={`${styles['dropdown-item']} ${isActiveRoute('/sub-distributor') ? styles.active : ''
                          }`}
                        onClick={() => handleNavigation('/sub-distributor')}
                      >
                        <span className={styles['menu-icon']}>
                          <NavSubDistributorIcon
                            width={20}
                            height={20}
                            color={
                              isActiveRoute('/sub-distributor')
                                ? '#313485'
                                : '#fff'
                            }
                          />
                        </span>
                        Sub Distributor
                      </li>
                    )}
                    {shouldShowAgent(userRole) && (
                      <li
                        className={`${styles['dropdown-item']} ${isActiveRoute('/agent') ? styles.active : ''
                          }`}
                        onClick={() => handleNavigation('/agent')}
                      >
                        <span className={styles['menu-icon']}>
                          <NavAgentIcon
                            width={20}
                            height={20}
                            color={isActiveRoute('/agent') ? '#313485' : '#fff'}
                          />
                        </span>
                        Agent
                      </li>
                    )}
                    {shouldShowShop(userRole) && (
                      <li
                        className={`${styles['dropdown-item']} ${isActiveRoute('/shop') ? styles.active : ''
                          }`}
                        onClick={() => handleNavigation('/shop')}
                      >
                        <span className={styles['menu-icon']}>
                          <NavShopIcon
                            width={20}
                            height={20}
                            color={isActiveRoute('/shop') ? '#313485' : '#fff'}
                          />
                        </span>
                        Shop
                      </li>
                    )}
                    {shouldShowPlayer(userRole) && (
                      <li
                        className={`${styles['dropdown-item']} ${isActiveRoute('/player') ? styles.active : ''
                          }`}
                        onClick={() => handleNavigation('/player')}
                      >
                        <span className={styles['menu-icon']}>
                          <NavPlayerIcon
                            width={20}
                            height={20}
                            color={
                              isActiveRoute('/player') ? '#313485' : '#fff'
                            }
                          />
                        </span>
                        Player
                      </li>
                    )}
                  </>
                )}
              <li
                className={isActiveRoute('/transactions') ? styles.active : ''}
                onClick={() => handleNavigation('/transactions')}
              >
                <span className={styles['menu-icon']}>
                  <NavTransactionsIcon
                    width={20}
                    height={20}
                    color={isActiveRoute('/transactions') ? '#313485' : '#fff'}
                  />
                </span>
                Transactions
              </li>
              {(brand !== 'flow888' || userRole === 'ADMIN') && (
                <li
                  className={isActiveRoute('/online-transactions') ? styles.active : ''}
                  onClick={() => handleNavigation('/online-transactions')}
                >
                  <span className={styles['menu-icon']}>
                    <NavOnlineTransactionsIcon
                      width={20}
                      height={20}
                      color={isActiveRoute('/online-transactions') ? '#313485' : '#fff'}
                    />
                  </span>
                  Online Transactions
                </li>
              )}
              {userRole !== 'SHOP' && (
                <li
                  className={isActiveRoute('/game-history') ? styles.active : ''}
                  onClick={() => handleNavigation('/game-history')}
                >
                  <span className={styles['menu-icon']}>
                    <NavGameHistoryIcon
                      width={20}
                      height={20}
                      color={isActiveRoute('/game-history') ? '#313485' : '#fff'}
                    />
                  </span>
                  Game History
                </li>
              )}
              {(userRole === 'ADMIN' ||
                authService.hasPermission('game.update')) && (
                  <li
                    className={
                      isActiveRoute('/game-management') ? styles.active : ''
                    }
                    onClick={() => handleNavigation('/game-management')}
                  >
                    <span className={styles['menu-icon']}>
                      <NavGameManagementIcon
                        width={20}
                        height={20}
                        color={
                          isActiveRoute('/game-management') ? '#313485' : '#fff'
                        }
                      />
                    </span>
                    Game Management
                  </li>
                )}
              </>)}
              {canSeeAtmController && (
                  <li
                    className={
                      isActiveRoute('/atm-controller') ? styles.active : ''
                    }
                    onClick={() => handleNavigation('/atm-controller')}
                  >
                    <span className={styles['menu-icon']}>
                      <NavAtmIcon
                        width={20}
                        height={20}
                        color={
                          isActiveRoute('/atm-controller') ? '#313485' : '#fff'
                        }
                      />
                    </span>
                    ATM Controller
                  </li>
                )}
              {!isAtmManager && (<>
              {userRole === 'ADMIN' && (
                  <li
                    className={
                      isActiveRoute('/atm-history') ? styles.active : ''
                    }
                    onClick={() => handleNavigation('/atm-history')}
                  >
                    <span className={styles['menu-icon']}>
                      <NavAtmIcon
                        width={20}
                        height={20}
                        color={
                          isActiveRoute('/atm-history') ? '#313485' : '#fff'
                        }
                      />
                    </span>
                    ATM History
                  </li>
                )}
              {userRole === 'ADMIN' && (
                <li
                  className={
                    isActiveRoute('/provider-balance') ? styles.active : ''
                  }
                  onClick={() => handleNavigation('/provider-balance')}
                >
                  <span className={styles['menu-icon']}>
                    <NavProviderBalanceIcon
                      width={20}
                      height={20}
                      color={
                        isActiveRoute('/provider-balance') ? '#313485' : '#fff'
                      }
                    />
                  </span>
                  Provider Balance
                </li>
              )}
              {userRole === 'ADMIN' && (
                <li
                  className={
                    isActiveRoute('/configurations') ? styles.active : ''
                  }
                  onClick={() => handleNavigation('/configurations')}
                >
                  <span className={styles['menu-icon']}>
                    <NavConfigurationsIcon
                      width={20}
                      height={20}
                      color={
                        isActiveRoute('/configurations') ? '#313485' : '#fff'
                      }
                    />
                  </span>
                  Configurations
                </li>
              )}
              {/* {console.log(authService.hasPermission('bonus.read'))} */}
              {authService.hasPermission('bonus.read') && (
                <li
                  className={
                    isActiveRoute('/promo') ? styles.active : ''
                  }
                  onClick={() => handleNavigation('/promo')}
                >
                  <span className={styles['menu-icon']}>
                    <NavPromoIcon
                      width={20}
                      height={20}
                      color={
                        isActiveRoute('/promo') ? '#313485' : '#fff'
                      }
                    />
                  </span>
                  Promo
                </li>
              )}
              {authService.hasPermission('bonus.read') && (
                <li
                  className={
                    isActiveRoute('/raffle') ? styles.active : ''
                  }
                  onClick={() => handleNavigation('/raffle')}
                >
                  <span className={styles['menu-icon']}>
                    <NavRaffleIcon
                      width={20}
                      height={20}
                      color={
                        isActiveRoute('/raffle') ? '#313485' : '#fff'
                      }
                    />
                  </span>
                  Raffle
                </li>
              )}
              {userRole === 'ADMIN' && (
                <li
                  className={
                    isActiveRoute('/notifications') ? styles.active : ''
                  }
                  onClick={() => handleNavigation('/notifications')}
                >
                  <span className={styles['menu-icon']}>
                    <NavNotificationsIcon
                      width={20}
                      height={20}
                      color={
                        isActiveRoute('/notifications') ? '#313485' : '#fff'
                      }
                    />
                  </span>
                  Notifications
                </li>
              )}
              {/* {userRole !== 'SHOP' && (
                <li
                  className={isActiveRoute('/jackpot') ? styles.active : ''}
                  onClick={() => handleNavigation('/jackpot')}
                >
                  <span className={styles['menu-icon']}>
                    <JeckpotIcon
                      width={24}
                      height={24}
                      color={isActiveRoute('/jackpot') ? '#313485' : '#fff'}
                    />
                  </span>
                  JackPot
                </li>
              )} */}
              {shouldShowPermissions(userRole) && (
                <li
                  className={isActiveRoute('/permissions') ? styles.active : ''}
                  onClick={() => handleNavigation('/permissions')}
                >
                  <span className={styles['menu-icon']}>
                    <NavPermissionsIcon
                      width={20}
                      height={20}
                      color={isActiveRoute('/permissions') ? '#313485' : '#fff'}
                    />
                  </span>
                  Permissions
                </li>
              )}
              <li
                className={isActiveRoute('/support') ? styles.active : ''}
                onClick={() => handleNavigation('/support')}
              >
                <span className={styles['menu-icon']}>
                  <NavSupportIcon
                    width={20}
                    height={20}
                    color={isActiveRoute('/support') ? '#313485' : '#fff'}
                  />
                </span>
                Support
              </li>
              {shouldShowPermissions(userRole) && (
                <li
                  className={isActiveRoute('/withdraw-review') ? styles.active : ''}
                  onClick={() => handleNavigation('/withdraw-review')}
                >
                  <span className={styles['menu-icon']}>
                    <NavWithdrawReviewIcon
                      width={20}
                      height={20}
                      color={isActiveRoute('/withdraw-review') ? '#313485' : '#fff'}
                    />
                  </span>
                  Withdraw Review
                </li>
              )}
              </>)}
            </ul>
          </nav>
          <div className={styles['sidebar-footer']}>
            <button
              type="button"
              className={styles['footer-link']}
              onClick={handleLogout}
            >
              Log Out
            </button>
            <button
              type="button"
              className={styles['footer-link']}
              onClick={handlePasswordModalOpen}
            >
              <span className={styles['menu-icon']}>
                <ChangePasswordIcon width={24} height={24} color="#fff" />
              </span>
              Change Password
            </button>
          </div>
        </aside>
        {/* Page Content */}
        <EnvironmentProvider>
          {children}
        </EnvironmentProvider>
      </div>

      {/* Change Password Modal */}
      {user && (
        <ChangePasswordModal
          isOpen={isPasswordModalOpen}
          onClose={handlePasswordModalClose}
          userId={user.id}
        />
      )}
    </div>
  );
};

export default Layout;
